import { Link } from 'react-router-dom';
import {
  EGB_SEC_ID,
  COUNCIL_ROLES,
  COUNCIL_FUNCTIONS,
  ZERO_TRUST_PILLARS,
  IDENTITY_ACCESS,
  SECRETS_GOVERNANCE,
  DATA_SECURITY,
  PRIVACY_GOVERNANCE,
  MARKETPLACE_SECURITY,
  AI_SECURITY,
  APPLICATION_SECURITY,
  INFRASTRUCTURE_SECURITY,
  SECOPS,
  INCIDENT_RESPONSE,
  VULNERABILITY_MANAGEMENT,
  COMPLIANCE_STANDARDS,
  BUSINESS_CONTINUITY,
  SECURITY_METRICS,
  SECURITY_AWARENESS,
  EXECUTIVE_DASHBOARD,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_COUNCIL,
  DEMO_ZERO_TRUST,
  DEMO_SECRETS_DATA,
  DEMO_PRIVACY_MARKETPLACE,
  DEMO_AI_SECURITY,
  DEMO_INFRA_SECOPS,
  DEMO_INCIDENT_VULN,
  DEMO_COMPLIANCE,
  buildSecurityDashboard,
} from '../../securitygovernance/framework';
import {
  SecurityCouncilPanel,
  ZeroTrustPanel,
  SecretsDataPanel,
  PrivacyMarketplacePanel,
  AISecurityPanel,
  InfrastructureSecOpsPanel,
  IncidentVulnerabilityPanel,
  ComplianceDashboardPanel,
} from '../../components/securitygovernance';
import { Card, KpiCard } from '../../components/ui';

export function SecurityGovernancePage() {
  const dashboard = buildSecurityDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{EGB_SEC_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Security Governance</h1>
        <p className="mt-1 text-sm text-slate-500">
          Permanent Security Constitution — Zero Trust, privacy, compliance, and cyber resilience.
          Fully aligned with MPC-00001, EGB-00001, EGB-00002, and EGB-00003.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Council Roles" value={dashboard.council_roles.toString()} />
        <KpiCard label="Zero Trust Pillars" value={dashboard.zero_trust_pillars.toString()} />
        <KpiCard label="Compliance Standards" value={dashboard.compliance_standards.toString()} />
        <KpiCard label="Security Metrics" value={dashboard.security_metrics.toString()} />
        <KpiCard label="Incident Phases" value={dashboard.incident_phases.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Security Architecture</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Security Governance Council</h2>
        <p className="mt-1 text-sm text-slate-500">{COUNCIL_ROLES.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{COUNCIL_FUNCTIONS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <SecurityCouncilPanel members={DEMO_COUNCIL} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Zero Trust & Identity Access</h2>
        <p className="mt-1 text-sm text-slate-500">{ZERO_TRUST_PILLARS.join(' · ')} · {IDENTITY_ACCESS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ZeroTrustPanel items={DEMO_ZERO_TRUST} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Secrets & Data Security</h2>
        <p className="mt-1 text-sm text-slate-500">{SECRETS_GOVERNANCE.join(' · ')} · {DATA_SECURITY.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <SecretsDataPanel items={DEMO_SECRETS_DATA} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Privacy & Marketplace Security</h2>
        <p className="mt-1 text-sm text-slate-500">{PRIVACY_GOVERNANCE.join(' · ')} · {MARKETPLACE_SECURITY.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <PrivacyMarketplacePanel items={DEMO_PRIVACY_MARKETPLACE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI & Application Security</h2>
        <p className="mt-1 text-sm text-slate-500">{AI_SECURITY.join(' · ')} · {APPLICATION_SECURITY.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AISecurityPanel items={DEMO_AI_SECURITY} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Infrastructure & Security Operations</h2>
        <p className="mt-1 text-sm text-slate-500">{INFRASTRUCTURE_SECURITY.join(' · ')} · {SECOPS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <InfrastructureSecOpsPanel items={DEMO_INFRA_SECOPS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Incident Response & Vulnerability Management</h2>
        <p className="mt-1 text-sm text-slate-500">{INCIDENT_RESPONSE.join(' · ')} · {VULNERABILITY_MANAGEMENT.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <IncidentVulnerabilityPanel items={DEMO_INCIDENT_VULN} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Compliance & Executive Security Dashboard</h2>
        <p className="mt-1 text-sm text-slate-500">
          {COMPLIANCE_STANDARDS.join(' · ')} · {BUSINESS_CONTINUITY.join(' · ')} · {SECURITY_METRICS.join(' · ')} · {EXECUTIVE_DASHBOARD.join(' · ')} · {SECURITY_AWARENESS.join(' · ')}
        </p>
        <Card className="mt-4" padding="sm">
          <ComplianceDashboardPanel items={DEMO_COMPLIANCE} />
        </Card>
      </section>
    </div>
  );
}
